import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBars, faPlus} from '@fortawesome/free-solid-svg-icons';
import {OverlayTrigger, Tooltip} from '@themesberg/react-bootstrap';
import {FormattedMessage} from 'react-intl';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {usePlan} from '../services/PlanContext';

// ─────────────────────────────────────────────────────────────────────────────
// CrudTable — tableau de liste responsive et piloté par configuration.
//
// Composant PUREMENT présentationnel : il ne formate ni ne traduit aucune donnée
// métier. Chaque page fournit ses colonnes (avec un éventuel render(item) qui
// renvoie un ReactNode déjà formaté/traduit : devise, libellé, badge, SVG…), ses
// actions et ses callbacks. CrudTable se charge du rendu :
//   • Desktop (≥ 768px) → <table> épuré, colonnes secondaires masquables.
//   • Mobile  (< 768px) → cartes empilées label/valeur (zéro scroll horizontal).
// Le glisser-déposer (react-beautiful-dnd) est conservé tel quel ; la page garde
// la gestion de l'ordre + la persistance (onDragEnd).
//
// Props :
//   items        object[]                  — lignes à afficher
//   columns      ColumnDef[]               — voir ci-dessous
//   actions      ActionDef[]               — boutons par ligne
//   toolbar      { onNew, newLabel, extraActions? }
//   empty        { icon, title, description }
//   draggable    bool                      — active le DnD (défaut false)
//   getRowId     (item, index) => string   — défaut String(item.id)
//   onDragEnd    (result) => void          — handler rbd de la page
//   statusKey    string                    — défaut 'active'
//   intl         object                    — requis (i18n des en-têtes/libellés)
//
// ColumnDef = {
//   key, header (id i18n ou texte littéral), render?(item)=>ReactNode,
//   type?:'status', numeric?:bool, priority?:1|2|3, hideBelow?:'xl'|'lg',
//   mobileHidden?:bool
// }
//   • priority : SEULEMENT pour le mobile (1 = titre de carte ; sinon ordre des
//     lignes label/valeur). N'a aucun effet sur le masquage desktop.
//   • hideBelow : masque la colonne sur le tableau DESKTOP sous 'xl' (1200px) ou
//     'lg' (992px). La donnée reste visible dans la carte mobile.
//   • mobileHidden : true => jamais en carte mobile ; (item)=>bool => masquage
//     conditionnel de la ligne mobile (ex. champ vide).
//   • mobileLabelOnly : en carte mobile, affiche render(item) comme libellé à
//     gauche (sans valeur à droite), pour montrer le vrai nom plutôt qu'un
//     intitulé d'en-tête générique.
// ActionDef = { key, icon, label (id i18n | (item)=>id), onClick(item),
//               intent?:'neutral'|'warn'|'danger', hidden?(item)=>bool }
// ─────────────────────────────────────────────────────────────────────────────

const HOVER = ['hover'];
const MOBILE_QUERY = '(max-width: 767.98px)';

// Bascule desktop/mobile : un seul arbre (donc un seul contexte DnD) monté à la fois.
const useIsMobile = () => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia(MOBILE_QUERY).matches : false;
  const [isMobile, setIsMobile] = React.useState(get);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = () => setIsMobile(mql.matches);
    handler();
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);
  return isMobile;
};

const intentClass = (intent) =>
  intent === 'warn' ? 'is-warn' : intent === 'danger' ? 'is-danger' : '';

const CrudTable = ({
  items = [],
  columns = [],
  actions = [],
  toolbar,
  empty,
  draggable = false,
  getRowId = (item) => String(item.id),
  onDragEnd,
  statusKey = 'active',
  intl,
}) => {
  const isMobile = useIsMobile();
  const {openSubscription} = usePlan();

  // i18n : si la chaîne est un id connu on traduit, sinon on l'affiche telle quelle
  // (ex. en-tête dynamique « € / KM »). Évite les erreurs react-intl sur id absent.
  const hasMsg = (id) => !!(intl && intl.messages && intl.messages[id] != null);
  const t = (id, values) => hasMsg(id) ? intl.formatMessage({id}, values) : id;
  const resolveHeader = (h) => t(h, {newLine: ' '});
  // En-tête de colonne (desktop) : sur deux lignes si headerMultiline (le jeton
  // {newLine} devient un <br/>), sinon une seule ligne. Le libellé mobile reste
  // toujours sur une ligne (resolveHeader).
  const headerNode = (col) => (col.headerMultiline && hasMsg(col.header))
    ? <FormattedMessage id={col.header} values={{newLine: <br/>}}/>
    : resolveHeader(col.header);
  const actionLabel = (a, item) => t(typeof a.label === 'function' ? a.label(item) : a.label);

  const visibleActions = (item) => actions.filter((a) => !(a.hidden && a.hidden(item)));
  const renderCell = (col, item) => (col.render ? col.render(item) : item[col.key]);

  const statusCol = columns.find((c) => c.type === 'status');
  const titleCol = columns.find((c) => c.priority === 1) || columns[0];
  const mobileFieldCols = columns
    .map((c, i) => ({c, i}))
    .filter(({c}) => c !== titleCol && c.type !== 'status' && c.mobileHidden !== true)
    .sort((a, b) => (a.c.priority || 99) - (b.c.priority || 99) || a.i - b.i)
    .map(({c}) => c);

  const colClasses = (col) => [
    col.numeric ? 'is-numeric' : '',
    col.hideBelow === 'xl' ? 'col-hide-xl' : '',
    col.hideBelow === 'lg' ? 'col-hide-lg' : '',
  ].filter(Boolean).join(' ');

  const renderPill = (item) => {
    const on = !!item[statusKey];
    return (
      <span className={`ccv2-crud-pill ${on ? 'is-on' : 'is-off'}`}>
        <span className="ccv2-crud-pill-dot"/>
        {t(on ? 'ACTIVE' : 'INACTIVE')}
      </span>
    );
  };

  // ── Barre d'outils ──
  const renderToolbar = () => {
    if (!toolbar) return null;
    return (
      <div className="ccv2-crud-toolbar">
        <div className="ccv2-crud-toolbar-actions">
          {/* Formule gratuite plafonnée : le bouton reste visible ; le clic ouvre la modale d'abonnement
              plutôt que le formulaire — on n'indique la limite qu'à la tentative de création (pas de
              bannière permanente qui laisserait croire à un quota dépassé). */}
          <button type="button" className="ccv2-btn ccv2-btn-primary"
                  onClick={toolbar.locked ? () => openSubscription('RESOURCE_LIMIT') : toolbar.onNew}>
            <FontAwesomeIcon icon={faPlus}/> {t(toolbar.newLabel)}
          </button>
          {(toolbar.extraActions || []).map((ea) => (
            <button key={ea.key} type="button" className="ccv2-btn ccv2-btn-secondary" onClick={ea.onClick}>
              {ea.icon && <FontAwesomeIcon icon={ea.icon}/>} {t(ea.label)}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ── État vide ──
  if (!items || items.length === 0) {
    return (
      <div>
        {renderToolbar()}
        {empty && (
          <div className="ccv2-crud-empty">
            <div className="ccv2-crud-empty-icon"><FontAwesomeIcon icon={empty.icon}/></div>
            <div className="ccv2-crud-empty-title">{t(empty.title)}</div>
            <div className="ccv2-crud-empty-desc">{t(empty.description)}</div>
            {toolbar && (
              <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={toolbar.locked ? () => openSubscription('RESOURCE_LIMIT') : toolbar.onNew}>
                <FontAwesomeIcon icon={faPlus}/> {t(toolbar.newLabel)}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Actions (fonctions de rendu, pas des composants imbriqués) ──
  const desktopActions = (item) => (
    <div className="ccv2-crud-actions">
      {visibleActions(item).map((a) => (
        <OverlayTrigger key={a.key} trigger={HOVER} overlay={<Tooltip>{actionLabel(a, item)}</Tooltip>}>
          <button type="button" className={`ccv2-crud-iconbtn ${intentClass(a.intent)}`}
                  aria-label={actionLabel(a, item)} onClick={() => a.onClick(item)}>
            <FontAwesomeIcon icon={a.icon}/>
          </button>
        </OverlayTrigger>
      ))}
    </div>
  );

  const mobileActions = (item) => (
    <div className="ccv2-crud-mobile-actions">
      {visibleActions(item).map((a) => (
        <button key={a.key} type="button" className={`ccv2-crud-mbtn ${intentClass(a.intent)}`}
                onClick={() => a.onClick(item)}>
          <FontAwesomeIcon icon={a.icon}/> <span>{actionLabel(a, item)}</span>
        </button>
      ))}
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // MOBILE — cartes empilées
  // ─────────────────────────────────────────────────────────────
  if (isMobile) {
    const card = (item, dragProvided) => (
      <div className="ccv2-crud-mobile-card"
           ref={dragProvided ? dragProvided.innerRef : undefined}
           {...(dragProvided ? dragProvided.draggableProps : {})}>
        <div className="ccv2-crud-mc-head">
          {draggable && dragProvided && (
            <span className="ccv2-crud-handle" {...dragProvided.dragHandleProps} aria-label={t('REORDER')}>
              <FontAwesomeIcon icon={faBars}/>
            </span>
          )}
          <div className="ccv2-crud-mc-title">{titleCol && renderCell(titleCol, item)}</div>
          {statusCol && renderPill(item)}
        </div>
        {mobileFieldCols.map((col) => {
          // Masquage conditionnel par ligne (ex. champ « Colonne 2 » vide)
          if (typeof col.mobileHidden === 'function' && col.mobileHidden(item)) return null;
          // mobileLabelOnly : la valeur tient lieu de libellé (à gauche, sans valeur
          // à droite) — utilisé pour afficher le vrai nom de colonne plutôt qu'un
          // intitulé générique (« Colonne 2 ») sur la page de réservation.
          if (col.mobileLabelOnly) {
            return (
              <div className="ccv2-crud-mv-row is-label-only" key={col.key}>
                <span className="ccv2-crud-mv-label">{renderCell(col, item)}</span>
              </div>
            );
          }
          return (
            <div className="ccv2-crud-mv-row" key={col.key}>
              <span className="ccv2-crud-mv-label">{resolveHeader(col.header)}</span>
              <span className={`ccv2-crud-mv-value ${col.numeric ? 'is-numeric' : ''}`}>{renderCell(col, item)}</span>
            </div>
          );
        })}
        {actions.length > 0 && mobileActions(item)}
      </div>
    );

    return (
      <div>
        {renderToolbar()}
        {draggable ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="table-body">
              {(provided) => (
                <div className="ccv2-crud-mobile-list" ref={provided.innerRef} {...provided.droppableProps}>
                  {items.map((item, index) => (
                    <Draggable draggableId={getRowId(item, index)} key={getRowId(item, index)} index={index}>
                      {(dragProvided) => card(item, dragProvided)}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="ccv2-crud-mobile-list">
            {items.map((item, index) => (
              <React.Fragment key={getRowId(item, index)}>{card(item)}</React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // DESKTOP — tableau
  // ─────────────────────────────────────────────────────────────
  const headerRow = (
    <tr>
      {draggable && <th className="ccv2-crud-th-handle" aria-hidden="true"/>}
      {columns.map((col) => (
        <th key={col.key} className={colClasses(col)}>{headerNode(col)}</th>
      ))}
      {actions.length > 0 && <th className="ccv2-crud-th-actions">{t('ACTIONS')}</th>}
    </tr>
  );

  const rowCells = (item) => (
    <>
      {columns.map((col) => (
        <td key={col.key} className={colClasses(col)}>
          {col.type === 'status' ? renderPill(item) : renderCell(col, item)}
        </td>
      ))}
      {actions.length > 0 && (
        <td className="ccv2-crud-td-actions">{desktopActions(item)}</td>
      )}
    </>
  );

  return (
    <div>
      {renderToolbar()}
      <div className="ccv2-crud-card">
        <div className="ccv2-crud-scroll">
          <table className="ccv2-crud-table">
            <thead>{headerRow}</thead>
            {draggable ? (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="table-body">
                  {(provided) => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                      {items.map((item, index) => (
                        <Draggable draggableId={getRowId(item, index)} key={getRowId(item, index)} index={index}>
                          {(dragProvided) => (
                            <tr ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                              <td className="ccv2-crud-td-handle">
                                <span className="ccv2-crud-handle" {...dragProvided.dragHandleProps} aria-label={t('REORDER')}>
                                  <FontAwesomeIcon icon={faBars}/>
                                </span>
                              </td>
                              {rowCells(item)}
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <tbody>
                {items.map((item, index) => (
                  <tr key={getRowId(item, index)}>{rowCells(item)}</tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrudTable;
